// Отправка событий аналитики v2 (MOB-470). Идёт рядом со старым Analytics.send
// и его не трогает. Контекст (слой 1 контракта) проставляется здесь, в месте
// вызова пишутся только параметры события из реестра.
import Analytics from '@web/services/Analytics/Analytics'
import TagManager from '@web/services/Analytics/modules/TagManager'
import Logger from '@web/common/Logger'
import { ANALYTICS_ENABLED } from '@web/common/config'
import { randomUUID } from '@web/common/randomUUID'
import { REGISTRY, TRACKING_VERSION, EventNameV2, ParamsOf } from '@web/services/Analytics/registry'
import type { EventDefinition } from '@web/services/Analytics/v2/params'
import { getCurrentPage } from '@web/services/Analytics/v2/currentPage'
import { stripAuthParams } from '@web/services/Analytics/stripAuthParams'
import { isEmbedded } from '@web/composition/useEmbedMode'

// gtag поднимается в Analytics.init — после загрузки пользователя, то есть уже
// после первой навигации. Всё, что отправлено раньше, ждёт здесь.
const MAX_PENDING = 50
let pending: Array<Record<string, unknown>> = []

function embedOf (): 'iframe' | 'webview' | undefined {
  if (window.self !== window.top) return 'iframe'
  return isEmbedded() ? 'webview' : undefined
}

// Аналитика никогда не бросает наружу: track() зовут из router.afterEach и
// обработчиков кликов, и исключение сорвало бы навигацию или переход.
export function track<N extends EventNameV2> (name: N, params: ParamsOf<N>): void {
  try {
    trackUnsafe(name, params)
  } catch (e) {
    Logger.error(`[Analytics v2] не удалось отправить "${name}"`, e)
  }
}

function trackUnsafe<N extends EventNameV2> (name: N, params: ParamsOf<N>): void {
  if (process.env.NODE_ENV !== 'production') {
    warnIfInvalid(name, params as unknown as Record<string, unknown>)
  }

  const event: Record<string, unknown> = {
    ...params,
    action: name,
    event_id: randomUUID(),
    tracking_version: TRACKING_VERSION,
    platform: 'web',
    ...(embedOf() ? { embed: embedOf() } : {}),
    traffic_type: Analytics.getTrafficType(),
    ...getCurrentPage()
  }
  // Токены автологина в аналитику не отправляем — как и в старом Analytics.send.
  Object.keys(event).forEach(key => { event[key] = stripAuthParams(event[key]) })

  if (ANALYTICS_ENABLED && !window.gtag) {
    if (pending.length < MAX_PENDING) pending.push(event)
    return
  }
  send(event)
}

// Вызывается сразу после Analytics.init (useApp.ts).
export function flushPending (): void {
  if (!window.gtag) return
  const events = pending
  pending = []
  events.forEach(event => {
    try {
      send(event)
    } catch (e) {
      Logger.error(`[Analytics v2] не удалось отправить отложенное "${String(event.action)}"`, e)
    }
  })
}

// Для тестов.
export function resetPending (): void {
  pending = []
}

function send (event: Record<string, unknown>): void {
  // TagManager.send типизирован под старые события (все значения — строки);
  // gtag принимает и числа, поэтому приводим тип, а не значения.
  TagManager.send(event as unknown as EventBase)
}

function warnIfInvalid (name: string, params: Record<string, unknown>): void {
  const definition: EventDefinition | undefined = (REGISTRY as Record<string, EventDefinition>)[name]
  if (!definition) {
    Logger.warn(`[Analytics v2] событие "${name}" не заведено в реестре`)
    return
  }
  Object.entries(definition.params).forEach(([key, spec]) => {
    const value = params[key]
    if (spec.required && (value === undefined || value === '')) {
      Logger.warn(`[Analytics v2] "${name}": нет обязательного параметра ${key}`)
    }
    if (value !== undefined && spec.type === 'enum' && !spec.values.includes(String(value))) {
      Logger.warn(`[Analytics v2] "${name}": ${key}=${String(value)} не из словаря`)
    }
  })
  Object.keys(params)
    .filter(key => !(key in definition.params))
    .forEach(key => Logger.warn(`[Analytics v2] "${name}": параметр ${key} не объявлен в реестре`))
}
